/**
 * Days↔hours for the weekly grid — the single place the conversion exists.
 *
 * Days are always the stored unit: every quantity the server sends and every
 * total it computes is in days. An organisation set to hours only changes what
 * the grid shows and what a cell accepts, so the conversion lives here rather
 * than being repeated wherever a number is rendered.
 *
 * `options` is the week payload's `options` object: `{ unit, step, show_hours,
 * hours_in_day }`.
 */

const HOURS_FALLBACK = 8;

export function hoursInDay(options) {
    const hours = Number(options?.hours_in_day);

    return Number.isFinite(hours) && hours > 0 ? hours : HOURS_FALLBACK;
}

export function entersInHours(options) {
    return options?.unit === 'hours';
}

/**
 * Trims trailing zeros so a whole day reads "1" rather than "1.00", while a
 * quarter still reads "0.25".
 */
export function formatNumber(value) {
    const number = Number(value);

    if (!Number.isFinite(number) || number === 0) {
        return '';
    }

    return String(Math.round(number * 100) / 100);
}

/**
 * The number a cell shows and edits, in whatever unit the user types in.
 */
export function toEntryUnit(days, options) {
    const number = Number(days) || 0;

    return entersInHours(options) ? Math.round(number * hoursInDay(options) * 100) / 100 : number;
}

/**
 * The reverse: the entry-unit figure a user typed, back in days. Rounded to two
 * decimals, which is what quantises a displayed hours figure onto the day
 * increment — and matches how the server converts the same value.
 */
export function toDays(value, options) {
    const number = Number(value) || 0;

    return entersInHours(options) ? Math.round((number / hoursInDay(options)) * 100) / 100 : number;
}

export function formatCell(days, options) {
    return formatNumber(toEntryUnit(days, options));
}

export function unitLabel(options) {
    return entersInHours(options) ? $t('hours') : $t('days');
}

/**
 * A total, plus its hour equivalent when the organisation asks for hours
 * alongside days. Kept off the cells and on the totals row only — a grid
 * carrying two numbers in every box is unreadable.
 */
export function formatTotal(days, options) {
    const primary = formatNumber(toEntryUnit(days, options)) || '0';

    if (entersInHours(options) || !options?.show_hours) {
        return primary;
    }

    const hours = formatNumber((Number(days) || 0) * hoursInDay(options));

    return hours === '' ? primary : `${primary} (${hours}h)`;
}

/**
 * Whether a typed value sits on the increment. The server validates this too;
 * checking here is what lets the grid refuse a keystroke without a round trip.
 *
 * Tested in days against `day_step`, never in the entry unit against `step`.
 * The two disagree whenever the working day is fractional: a quarter of 7.5
 * hours is 1.875, a cell can only show 1.88, and 1.88 is not a whole multiple
 * of 1.875 — so testing in hours would reject the figure the grid displayed.
 */
export function isOnStep(value, options) {
    const step = Number(options?.day_step);

    if (!Number.isFinite(step) || step <= 0) {
        return true;
    }

    const steps = toDays(value, options) / step;

    return Math.abs(steps - Math.round(steps)) < 0.0001;
}
